/**
 * ContactDataSectionPure
 *
 * Pure presentational component for the contact data tab.
 * Receives all data and callbacks via props instead of using tRPC hooks.
 */
import type { ReactNode } from 'react';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';

import { formatAum } from '@poseidon/shared-utils';

import type { LinkedInMapping } from '../LinkedInIdentifiersSection';
import type { ContactEducationItem, ContactExperienceItem } from './ContactEmploymentEducationSectionPure';
import type { AddExperienceInput, OrgSearchOption } from './AddExperiencePopup';

type AddExperienceCallback = (input: AddExperienceInput) => Promise<void>;
type OrgSearchCallback = (query: string) => Promise<OrgSearchOption[]>;
import type { ContactSignalGridItem } from './ContactSignalsGrid';
import type { Contact, ContactFieldName, MeetingBookedData, PendingActionData } from './types';
import { cn } from '../cn';
import Button from '../Input2/Button';
import {
  ContactFieldAddRow,
  ContactFieldExpandedRows,
  ContactFieldReadOnly,
} from './ContactFieldReadOnly';
import { formatPhoneDisplay } from './formatPhoneDisplay';
import ToggleSwitch from '../Input2/ToggleSwitch';
import Tooltip from '../Input2/Tooltip';
import { LinkedInIdentifiersSectionPure } from '../LinkedInIdentifiersSection';
import Popup from '../Popup/Popup';
import { Scrollable } from '../Scrollable';
import { Alerts, Arrows, Communication, Finance, General, Maps, Time, Users } from '../StrokeIcons';
import { CompactMetadataBar } from './CompactMetadataBar';
import { ContactEmploymentEducationSectionPure } from './ContactEmploymentEducationSectionPure';
import { ContactSignalsGrid } from './ContactSignalsGrid';

// ============================================================================
// Types
// ============================================================================

export type ContactDataSectionPureProps = {
  // Core data
  contact: Contact;
  authUserId: string;
  authIsPremium: boolean;
  authCreditsRemaining: number;
  authUnipileAccountId: string | null | undefined;
  authContactDisqualified: boolean;

  // Meeting booked data
  meetingBookedDetails: MeetingBookedData[] | null;
  pendingAction?: PendingActionData | null;

  // Flags
  hasCadence: boolean;

  // Mutation callbacks (from ContactDetailMutations)
  onToggleTracking: (tracked: boolean) => Promise<void>;
  onToggleDisqualified: (disqualified: boolean) => Promise<void>;
  onAddFieldValue: (field: ContactFieldName, value: string, source: string) => Promise<void>;
  onDeleteFieldValue: (field: ContactFieldName, source: string) => Promise<void>;
  /**
   * Move a phone number to the contact's organization as a firm line instead of deleting it. When
   * provided, deleting a phone opens a popup offering this as an alternative; when omitted (no org
   * linked), the popup only offers a plain delete. `contactOrgName` labels the org in the popup.
   */
  onAssignPhoneToOrg?: (source: string) => Promise<void>;
  contactOrgName?: string | null;
  onMakeFieldPrimary: (field: ContactFieldName, source: string) => Promise<void>;
  onRemoveFieldPrimary: (field: ContactFieldName, source: string) => Promise<void>;
  onVerifyEmail: (field: ContactFieldName, source: string) => Promise<void>;
  /** When provided, the phone field shows a "Mark as firm number" action per value. Receives the
      phone number. Omit to hide the action (e.g. surfaces without the mutation wired). */
  onMarkFirmNumber?: (phone: string) => Promise<void>;
  onRefreshFromLinkedIn: () => Promise<void>;
  onEnhanceContact: () => Promise<void>;
  onDeleteMeetingBooked: (interactionId: number) => Promise<void>;
  onAgeOverride: (minAge: number | null) => Promise<void>;
  onResetGender: () => Promise<void>;

  // Loading states
  isRefreshing: boolean;
  isEnhancing: boolean;
  isTrackingPending: boolean;
  isDisqualifiedPending: boolean;
  isAgeOverridePending: boolean;
  isResetGenderPending: boolean;

  // Toast callbacks for field copy operations
  onFieldCopied?: (data: string) => void;
  onFieldCopyFailed?: (data: string) => void;

  // LinkedIn Identifiers section data and callbacks
  linkedInMappings: LinkedInMapping[];
  linkedinUnavailable?: boolean;
  isLinkedInMappingsLoading: boolean;
  onDeleteLinkedInMapping: (mappingId: number) => void;
  onVerifyLinkedInMapping: (mappingId: number) => void;
  onCopyLinkedInMapping: (foreignId: string) => void;
  onAddLinkedInMapping: (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => void;
  isLinkedInMappingDeleting: boolean;
  isLinkedInMappingVerifying: boolean;
  isLinkedInMappingAdding: boolean;

  // Optional superadmin copy feature
  onCopyToSuperadminTeam?: () => void;
  isCopyPending?: boolean;
  superadminName?: string;
  superadminTeamName?: string;

  // Optional superadmin destructive delete. When provided, a red "Delete
  // Contact" button renders below the LinkedIn identifiers; clicking it opens a
  // confirmation dialog and, on confirm, calls onDeleteContact. The parent gates
  // this on superadmin/impersonation and performs the deletion + navigation.
  onDeleteContact?: () => void;
  isDeletingContact?: boolean;

  // View all campaigns
  onViewAllCampaigns?: () => void;

  // View required actions
  onViewRequiredActions?: () => void;

  // Unipile connection callback (shown when not connected)
  onConnectUnipile?: () => void;

  // Tags section render prop
  renderContactTags?: () => React.ReactNode;

  // Employment & education history (rendered below the LinkedIn Identifiers section)
  experiences?: ContactExperienceItem[];
  education?: ContactEducationItem[];
  isHistoryLoading?: boolean;
  onAddExperience?: AddExperienceCallback;
  orgSearch?: OrgSearchCallback;
  onDeleteExperience?: (experienceId: string) => Promise<void>;

  // Signals (rendered as a 3x3 grid below the Track LinkedIn toggle when signals is provided)
  signals?: ContactSignalGridItem[];
  isSignalsLoading?: boolean;
  lastUnipileFetchAt?: Date | string | null;
  ignoredAspectKeys?: ReadonlySet<string>;

};

// ============================================================================
// ContactDataSectionPure
// ============================================================================

export function ContactDataSectionPure({
  contact,
  authUserId,
  // The Refresh & Enrich block moved to the tab row in ContactDetailPageCore. These props
  // are still in the type signature (caller passes them) but no longer used here.
  authIsPremium: _authIsPremium,
  authCreditsRemaining: _authCreditsRemaining,
  authUnipileAccountId: _authUnipileAccountId,
  authContactDisqualified,
  meetingBookedDetails,
  pendingAction,
  hasCadence,
  onToggleTracking,
  onToggleDisqualified,
  onAddFieldValue,
  onDeleteFieldValue,
  onAssignPhoneToOrg,
  contactOrgName,
  onMakeFieldPrimary,
  onRemoveFieldPrimary,
  onVerifyEmail,
  onMarkFirmNumber,
  onRefreshFromLinkedIn: _onRefreshFromLinkedIn,
  onEnhanceContact: _onEnhanceContact,
  onDeleteMeetingBooked,
  onAgeOverride,
  onResetGender,
  isRefreshing: _isRefreshing,
  isEnhancing: _isEnhancing,
  isTrackingPending,
  isDisqualifiedPending,
  isAgeOverridePending,
  isResetGenderPending,
  onFieldCopied: _onFieldCopied,
  onFieldCopyFailed: _onFieldCopyFailed,
  linkedInMappings,
  linkedinUnavailable,
  isLinkedInMappingsLoading,
  onDeleteLinkedInMapping,
  onVerifyLinkedInMapping,
  onCopyLinkedInMapping,
  onAddLinkedInMapping,
  isLinkedInMappingDeleting,
  isLinkedInMappingVerifying,
  isLinkedInMappingAdding,
  onCopyToSuperadminTeam,
  isCopyPending,
  superadminName,
  superadminTeamName,
  onDeleteContact,
  isDeletingContact,
  onViewAllCampaigns,
  onViewRequiredActions,
  onConnectUnipile: _onConnectUnipile,
  renderContactTags,
  experiences,
  education,
  isHistoryLoading,
  onAddExperience,
  orgSearch,
  onDeleteExperience,
  signals,
  isSignalsLoading,
  lastUnipileFetchAt,
  ignoredAspectKeys,
}: ContactDataSectionPureProps) {
  const isTrackingButtonDisabled = hasCadence;
  const [openNameField, setOpenNameField] = useState<'firstName' | 'lastName' | null>(null);
  const [addingNameField, setAddingNameField] = useState<'firstName' | 'lastName' | null>(null);
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);
  // Deleting a phone routes through a popup (delete permanently vs. move to the org as a firm
  // line) instead of removing it immediately. Holds the source of the phone pending that choice.
  const [phoneDeleteSource, setPhoneDeleteSource] = useState<string | null>(null);
  const phoneDeleteValue =
    phoneDeleteSource != null
      ? (contact.fields?.phone?.find((f) => f.source === phoneDeleteSource)?.data ?? null)
      : null;
  const contactDisplayName =
    [contact.primaryFields?.firstName?.data, contact.primaryFields?.lastName?.data]
      .filter(Boolean)
      .join(' ')
      .trim() || 'this contact';

  // If headline and title have the same primary value, collapse into one field
  const headlineValue = contact.primaryFields?.headline?.data?.trim();
  const titleValue = contact.primaryFields?.title?.data?.trim();
  const headlineTitleMatch = headlineValue && titleValue && headlineValue === titleValue;

  const fields: {
    name: ContactFieldName;
    placeholder: string;
    icon: ReactNode;
    value?: string;
    disabled?: boolean;
    allowVerify?: boolean;
  }[] = [
    { name: 'professionalEmail', placeholder: 'Professional Email', icon: <Communication.Mail01 />, allowVerify: true },
    { name: 'linkedinEmail', placeholder: 'Linkedin Email', icon: <Communication.Mail01 /> },
    { name: 'phone', placeholder: 'Phone', icon: <Communication.Phone /> },
    { name: 'firstName', placeholder: 'First Name', icon: <Users.User01 /> },
    { name: 'lastName', placeholder: 'Last Name', icon: <Users.User01 /> },
    {
      name: 'headline',
      placeholder: headlineTitleMatch ? 'Headline / Title' : 'Headline',
      icon: <General.Building02 />,
    },
    { name: 'cityFull', placeholder: 'Location', icon: <Maps.Map01 /> },
  ];

  // Add a divider after these fields
  const dividerAfter = new Set<ContactFieldName>(['phone']);

  // CompactMetadataBar values
  const probability =
    contact.liActiveUserProbability != null ? Math.round(contact.liActiveUserProbability * 100) : null;
  const age = contact.ageEstimate?.minAge ?? null;
  const hasAgeOverride = contact.ageEstimate?.source === 'override';
  const genderRaw = contact.primaryFields?.gender?.data;
  const gender: 'M' | 'F' | '?' = genderRaw === 'male' ? 'M' : genderRaw === 'female' ? 'F' : '?';
  const title = contact.primaryFields?.honorificTitle?.data ?? '—';

  const connectionsCount = contact.liConnectionsCount ?? 0;
  const recentPostsCount = contact.liRecentPostsCount ?? 0;
  const hasPicture = contact.primaryPictureUrl != null;
  const pictureContribution = hasPicture ? 20 : 0;
  const postsContribution = Math.min(40, recentPostsCount * 10);
  const connectionsContribution = Math.min(40, (connectionsCount / 500) * 40);

  const probabilityTooltip =
    probability != null ? (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className={hasPicture ? 'text-green-400' : 'text-gray-500'}>{hasPicture ? '✓' : '✗'}</span>
          <span>Profile picture</span>
          {hasPicture && <span className="text-green-400">+{pictureContribution}%</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={recentPostsCount > 0 ? 'text-green-400' : 'text-gray-500'}>
            {recentPostsCount > 0 ? '✓' : '✗'}
          </span>
          <span>Recent posts: {recentPostsCount}</span>
          {recentPostsCount > 0 && <span className="text-green-400">+{Math.round(postsContribution)}%</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={connectionsCount > 0 ? 'text-green-400' : 'text-gray-500'}>
            {connectionsCount > 0 ? '✓' : '✗'}
          </span>
          <span>Connections: {connectionsCount.toLocaleString()}</span>
          {connectionsCount > 0 && <span className="text-green-400">+{Math.round(connectionsContribution)}%</span>}
        </div>
      </div>
    ) : (
      'Research from LinkedIn to calculate'
    );

  const ageTooltip = hasAgeOverride
    ? 'Manually set — click pencil to edit'
    : age != null
      ? 'Calculated from work history — click pencil to override'
      : 'Unknown — research from LinkedIn';

  return (
    <Scrollable>
      <CompactMetadataBar
        probability={probability}
        age={age}
        gender={gender}
        title={title}
        probabilityTooltip={probabilityTooltip}
        ageTooltip={ageTooltip}
        hasAgeOverride={hasAgeOverride}
        onAgeOverride={onAgeOverride}
        isAgeOverridePending={isAgeOverridePending}
        onResetGender={onResetGender}
        isResetGenderPending={isResetGenderPending}
      />

      {/* Refresh & Enrich button moved to the tab row in ContactDetailPageCore (next to the
          zap/clock/person tabs and notes pencil) — kept compact and contextual to the page header
          rather than taking up a full body section. */}

      {fields.map((input) => {
        if (input.name === 'lastName') return null;

        if (input.name === 'firstName') {
          const lastNameField = fields.find((f) => f.name === 'lastName');
          const openValues =
            openNameField != null
              ? (contact.fields?.[openNameField] ?? []).filter((v) => !v.primary)
              : [];
          return (
            <Fragment key="name-pair">
              <div className="grid grid-cols-2 gap-0">
                <ContactFieldReadOnly
                  title={input.placeholder}
                  icon={input.icon}
                  fields={contact.fields?.[input.name]}
                  onRemovePrimary={(source) => onRemoveFieldPrimary(input.name, source)}
                  onMakePrimary={(source) => onMakeFieldPrimary(input.name, source)}
                  onDelete={(source) => onDeleteFieldValue(input.name, source)}
                  onAdd={(value) => onAddFieldValue(input.name, value, 'input')}
                  expanded={openNameField === 'firstName'}
                  onExpandedChange={(next) => {
                    setOpenNameField(next ? 'firstName' : null);
                    if (!next) setAddingNameField(null);
                  }}
                  isAdding={addingNameField === 'firstName'}
                  onIsAddingChange={(next) => {
                    setAddingNameField(next ? 'firstName' : null);
                    if (next) setOpenNameField('firstName');
                  }}
                  hideInlineExpandedPanel
                />
                {lastNameField && (
                  <ContactFieldReadOnly
                    title={lastNameField.placeholder}
                    icon={lastNameField.icon}
                    fields={contact.fields?.[lastNameField.name]}
                    onRemovePrimary={(source) => onRemoveFieldPrimary(lastNameField.name, source)}
                    onMakePrimary={(source) => onMakeFieldPrimary(lastNameField.name, source)}
                    onDelete={(source) => onDeleteFieldValue(lastNameField.name, source)}
                    onAdd={(value) => onAddFieldValue(lastNameField.name, value, 'input')}
                    expanded={openNameField === 'lastName'}
                    onExpandedChange={(next) => {
                      setOpenNameField(next ? 'lastName' : null);
                      if (!next) setAddingNameField(null);
                    }}
                    isAdding={addingNameField === 'lastName'}
                    onIsAddingChange={(next) => {
                      setAddingNameField(next ? 'lastName' : null);
                      if (next) setOpenNameField('lastName');
                    }}
                    hideInlineExpandedPanel
                  />
                )}
              </div>
              {openNameField != null && (openValues.length > 0 || addingNameField != null) && (
                <div
                  className={cn(
                    "relative isolate px-4 pt-0 pb-2 before:absolute before:inset-x-2 before:inset-y-0 before:-z-10 before:rounded-b-xl before:bg-slate-100 before:content-[''] dark:before:bg-slate-800/40",
                    openNameField === 'firstName' ? 'before:rounded-tr-xl' : 'before:rounded-tl-xl',
                  )}>
                  <ContactFieldExpandedRows
                    others={openValues}
                    onMakePrimary={(source) => onMakeFieldPrimary(openNameField, source)}
                    onDelete={(source) => onDeleteFieldValue(openNameField, source)}
                    addRow={
                      addingNameField === openNameField ? (
                        <ContactFieldAddRow
                          onSave={(value) => onAddFieldValue(openNameField, value, 'input')}
                          onCancel={() => setAddingNameField(null)}
                        />
                      ) : undefined
                    }
                  />
                </div>
              )}
            </Fragment>
          );
        }

        return (
          <Fragment key={input.name}>
            <ContactFieldReadOnly
              title={input.placeholder}
              icon={input.icon}
              fields={contact.fields?.[input.name]}
              onRemovePrimary={(source) => onRemoveFieldPrimary(input.name, source)}
              onMakePrimary={(source) => onMakeFieldPrimary(input.name, source)}
              // Deleting a phone opens the delete-vs-move-to-org popup, but only when there's an
              // org to move it to (onAssignPhoneToOrg provided). Otherwise — and for every other
              // field — the value deletes immediately, so we never show a choiceless confirmation.
              onDelete={
                input.name === 'phone' && onAssignPhoneToOrg != null
                  ? (source) => setPhoneDeleteSource(source)
                  : (source) => onDeleteFieldValue(input.name, source)
              }
              onAdd={(value) => onAddFieldValue(input.name, value, 'input')}
              onVerify={input.allowVerify ? (source) => onVerifyEmail(input.name, source) : undefined}
              onMarkFirmNumber={input.name === 'phone' ? onMarkFirmNumber : undefined}
              formatValue={input.name === 'phone' ? formatPhoneDisplay : undefined}
            />
            {dividerAfter.has(input.name) && (
              <div className="mx-4 my-1 border-t border-gray-100 dark:border-gray-800" />
            )}
          </Fragment>
        );
      })}

      {/* Timezone — computed from the contact's city/state (read-only). Shows their current local
          time so a rep knows whether it's a good moment to call. Hidden when we can't resolve one. */}
      {contact.timezone != null && contact.timezone !== '' && (
        <div className="flex items-center gap-2.5 px-4 py-1.5 text-sm">
          <span className="shrink-0 text-neutral-400 dark:text-neutral-500">
            <Time.Clock className="size-4" />
          </span>
          <span className="text-[10px] uppercase tracking-wide text-haiti-500 dark:text-haiti-400">Timezone</span>
          <span className="ml-auto text-neutral-600 dark:text-neutral-200">
            {formatContactTimezone(contact.timezone)}
          </span>
        </div>
      )}

      {/* Estimated AUM — the contact's effective firm AUM (their own value, else their org's),
          read-only. Sourced from a CSV firm-AUM column or AdvisorGuide enrichment. Hidden when unknown;
          a non-positive value (e.g. a `parseAum('0')` from a blank CSV cell) is treated as unknown, not "$0". */}
      {contact.estimatedAum != null && contact.estimatedAum > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-1.5 text-sm">
          <span className="shrink-0 text-neutral-400 dark:text-neutral-500">
            <Finance.Coins01 className="size-4" />
          </span>
          <span className="text-[10px] uppercase tracking-wide text-haiti-500 dark:text-haiti-400">Est. AUM</span>
          <span className="ml-auto text-neutral-600 dark:text-neutral-200">{formatAum(contact.estimatedAum)}</span>
        </div>
      )}

      {/* Signals — moved high up because they're the most decision-critical info on the page.
          The dense LinkedIn Identifier rows used to live here but now live at the bottom (collapsed). */}
      {signals != null && (
        <ContactSignalsGrid
          signals={signals}
          isLoading={isSignalsLoading}
          lastUnipileFetchAt={lastUnipileFetchAt}
          ignoredAspectKeys={ignoredAspectKeys}
        />
      )}

      <ContactEmploymentEducationSectionPure
        experiences={experiences ?? []}
        education={education ?? []}
        isLoading={isHistoryLoading ?? false}
        onAddExperience={onAddExperience}
        orgSearch={orgSearch}
        onDeleteExperience={onDeleteExperience}
        contactFirstName={contact.primaryFields?.firstName?.data}
      />

      {renderContactTags && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">{renderContactTags()}</div>
      )}

      <div className="mt-1 flex flex-col gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
        <div className="flex flex-col gap-4">
          <Tooltip tooltip="Mark this contact as disqualified to exclude them from future campaigns and analytics">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Disqualified</span>
              <ToggleSwitch
                size="sm"
                checked={authContactDisqualified}
                disabled={isDisqualifiedPending}
                onChange={onToggleDisqualified}
              />
            </div>
          </Tooltip>
          <Tooltip
            tooltip={
              isTrackingButtonDisabled
                ? 'Automatically enabled when contact is in an active campaign. LinkedIn activity is monitored to detect responses and engagement.'
                : "Enable to track this contact's LinkedIn activity and engagement outside of campaigns"
            }>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Track LinkedIn{isTrackingButtonDisabled ? ' (via campaign)' : ''}
              </span>
              <ToggleSwitch
                size="sm"
                checked={Boolean(isTrackingButtonDisabled || contact.tracked)}
                disabled={isTrackingButtonDisabled || isTrackingPending}
                onChange={onToggleTracking}
              />
            </div>
          </Tooltip>
          {meetingBookedDetails && meetingBookedDetails.length > 0 && (
            <MeetingBookedIndicator
              meetings={meetingBookedDetails}
              contactId={contact.id}
              currentUserId={authUserId}
              onDeleteMeetingBooked={onDeleteMeetingBooked}
              pendingAction={pendingAction}
            />
          )}
          {onCopyToSuperadminTeam && (
            <Button
              text
              icon={<General.Copy01 className="size-4" />}
              onClick={onCopyToSuperadminTeam}
              disabled={isCopyPending}
              tooltip={`Copy to ${superadminName ?? 'superadmin'}'s team${superadminTeamName ? ` (${superadminTeamName})` : ''}`}
              className="justify-start text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300">
              Copy to My Team
            </Button>
          )}
        </div>
      </div>

      {onViewAllCampaigns && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <Button
            text
            icon={<Time.ClockRewind className="size-4" />}
            onClick={onViewAllCampaigns}
            className="justify-start text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            View Current & Past Campaigns
          </Button>
        </div>
      )}

      {onViewRequiredActions && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <Button
            text
            icon={<General.CheckCircle className="size-4" />}
            onClick={onViewRequiredActions}
            className="justify-start text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Actions Needed
          </Button>
        </div>
      )}

      {/* LinkedIn Identifiers — moved to the bottom and collapsed by default. Most users
          don't need to see URN IDs day-to-day; the section auto-expands if a mapping is failing. */}
      <LinkedInIdentifiersSectionPure
        mappings={linkedInMappings}
        linkedinUnavailable={linkedinUnavailable}
        isLoading={isLinkedInMappingsLoading}
        onDelete={onDeleteLinkedInMapping}
        onVerify={onVerifyLinkedInMapping}
        onCopy={onCopyLinkedInMapping}
        onAdd={onAddLinkedInMapping}
        isDeleting={isLinkedInMappingDeleting}
        isVerifying={isLinkedInMappingVerifying}
        isAdding={isLinkedInMappingAdding}
      />

      {/* Superadmin-only: permanently delete the contact. The parent gates this
          on superadmin/impersonation by only passing onDeleteContact then. */}
      {onDeleteContact && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <Button
            text
            icon={<General.Trash01 className="size-4" />}
            onClick={() => setShowDeleteContactConfirm(true)}
            disabled={isDeletingContact}
            className="justify-start text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
            Delete Contact
          </Button>
        </div>
      )}

      {onDeleteContact && showDeleteContactConfirm && (
        <Popup onClose={() => setShowDeleteContactConfirm(false)} className="max-w-sm p-0">
          <div className="flex flex-col items-center gap-2 px-5 pt-5 text-center">
            <div className="flex size-9 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <Alerts.AlertTriangle className="size-4 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete this contact?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This permanently deletes <span className="font-medium text-gray-700 dark:text-gray-300">{contactDisplayName}</span> and
              all associated data (campaigns, messages, LinkedIn identifiers, and history). This cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4">
            <Button size="sm" onClick={() => setShowDeleteContactConfirm(false)} disabled={isDeletingContact}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="red"
              icon={<General.Trash01 />}
              disabled={isDeletingContact}
              onClick={() => {
                setShowDeleteContactConfirm(false);
                onDeleteContact();
              }}>
              Delete
            </Button>
          </div>
        </Popup>
      )}

      {phoneDeleteSource != null && (
        <Popup onClose={() => setPhoneDeleteSource(null)} className="max-w-sm p-0">
          <div className="flex flex-col items-center gap-2 px-5 pt-5 text-center">
            <div className="flex size-9 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <Communication.Phone className="size-4 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Remove this phone number?</h3>
            {phoneDeleteValue != null && (
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formatPhoneDisplay(phoneDeleteValue)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 px-5 py-4">
            <Button
              color="red"
              icon={<General.Trash01 />}
              onClick={() => {
                if (phoneDeleteSource == null) return;
                const source = phoneDeleteSource;
                setPhoneDeleteSource(null);
                void onDeleteFieldValue('phone', source);
              }}>
              Delete permanently
            </Button>
            {onAssignPhoneToOrg && (
              <Button
                color="blue"
                icon={<General.Building02 />}
                onClick={() => {
                  if (phoneDeleteSource == null) return;
                  const source = phoneDeleteSource;
                  setPhoneDeleteSource(null);
                  void onAssignPhoneToOrg(source);
                }}>
                Firm number — move to {contactOrgName?.trim() ? contactOrgName : 'organization'}
              </Button>
            )}
            <Button text onClick={() => setPhoneDeleteSource(null)}>
              Cancel
            </Button>
          </div>
        </Popup>
      )}

    </Scrollable>
  );
}

// ============================================================================
// TriggerMessageCard
// ============================================================================

function formatShortDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Render an IANA timezone as "<contact's current local time> · <city>" (e.g. "2:45 PM · Denver"),
 * so a rep sees whether it's a decent moment to call. Falls back to just the city label if the
 * timezone string is invalid.
 */
export function formatContactTimezone(timezone: string): string {
  const city = timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone;
  try {
    const time = new Date().toLocaleTimeString('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit' });
    return `${time} · ${city}`;
  } catch {
    return city;
  }
}

function TriggerMessageCard({ body, isSent, date }: { body: string; isSent: boolean; date: Date | null }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = body.length > 120;
  const displayBody = expanded || !needsTruncation ? body : body.slice(0, 120) + '...';

  return (
    <div
      onClick={needsTruncation ? () => setExpanded((v) => !v) : undefined}
      className={cn(
        'rounded-sm border p-2 border-blue-100 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-900/10',
        needsTruncation && 'cursor-pointer hover:opacity-80',
      )}>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400">
        {isSent && <Communication.Send01 className="size-3" />}
        {!isSent && <Arrows.CornerUpLeft className="size-3" />}
        <span>Triggered by {isSent ? 'sent' : 'received'} message</span>
        {date && <span className="text-slate-400 dark:text-slate-500">{formatShortDate(date)}</span>}
      </div>
      <p className="whitespace-pre-wrap text-xs text-slate-700 dark:text-slate-300">{displayBody}</p>
      {needsTruncation && !expanded && (
        <span className="mt-1 inline-block text-xs font-medium text-blue-500 dark:text-blue-400">
          Click to expand
        </span>
      )}
    </div>
  );
}

// ============================================================================
// MeetingBookedIndicator
// ============================================================================

type MeetingBookedIndicatorProps = {
  meetings: MeetingBookedData[];
  contactId: string;
  currentUserId: string;
  onDeleteMeetingBooked: (interactionId: number) => Promise<void>;
  pendingAction?: PendingActionData | null;
};

function MeetingBookedIndicator({
  meetings,
  contactId,
  currentUserId,
  onDeleteMeetingBooked,
  pendingAction,
}: MeetingBookedIndicatorProps) {
  const isSent = pendingAction?.sourceDirection === 'sent';

  return (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <General.Zap className="size-4" />
        <span className="font-medium">Prospect interested</span>
      </div>
      {pendingAction && (
        <div className="flex flex-col gap-1.5 pl-6">
          <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
            {pendingAction.actionText}
          </span>
          {pendingAction.sourceBody && (
            <TriggerMessageCard
              body={pendingAction.sourceBody}
              isSent={isSent}
              date={pendingAction.sourceDate}
            />
          )}
        </div>
      )}
      {meetings.map((meeting) => (
        <MeetingBookedItem
          key={meeting.interactionId}
          meeting={meeting}
          contactId={contactId}
          currentUserId={currentUserId}
          onDelete={onDeleteMeetingBooked}
        />
      ))}
    </div>
  );
}

// ============================================================================
// MeetingBookedItem
// ============================================================================

type MeetingBookedItemProps = {
  meeting: MeetingBookedData;
  contactId: string;
  currentUserId: string;
  onDelete: (interactionId: number) => Promise<void>;
};

function MeetingBookedItem({ meeting, currentUserId, onDelete }: MeetingBookedItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const messageText = meeting.triggerMessage?.body || meeting.triggerMessage?.subject || '';
  const canDelete = meeting.userId === currentUserId;

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [messageText]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(meeting.interactionId);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="group flex items-start justify-between gap-2 pl-6">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 dark:text-gray-400">by {meeting.bookedByName}</p>
        {messageText && (
          <p
            ref={textRef}
            className={cn(
              'text-xs text-gray-400 dark:text-gray-500',
              !isExpanded && 'line-clamp-2',
              isTruncated && !isExpanded && 'cursor-pointer hover:text-gray-500 dark:hover:text-gray-400',
            )}
            onClick={isTruncated ? () => setIsExpanded(!isExpanded) : undefined}>
            "{messageText}"
          </p>
        )}
      </div>
      {canDelete && (
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="shrink-0 text-gray-400 opacity-0 transition-opacity hover:text-red-500 disabled:opacity-50 group-hover:opacity-100"
          title="Remove interest detected">
          <General.Trash01 className="size-3" />
        </button>
      )}
    </div>
  );
}
